'use client';

import { ExternalLinkIcon, EyeIcon, EyeOffIcon, KeyIcon, Trash2Icon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { ScenarioCredentialStatus, ScenarioRevealedCredentials } from '@/app/sdk';
import { sdk } from '@/app/sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
const SCENARIO_SETTINGS_URL = 'https://app.scenario.com/settings/api';
interface ScenarioCredentialsSectionProps {
  active: boolean;
}
export function ScenarioCredentialsSection({
  active
}: ScenarioCredentialsSectionProps) {
  const [status, setStatus] = useState<ScenarioCredentialStatus | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [secret, setSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [revealed, setRevealed] = useState<ScenarioRevealedCredentials | null>(null);
  const [revealStored, setRevealStored] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const refreshStatus = useCallback(async () => {
    if (!sdk?.scenario?.getCredentialStatus) {
      setStatus(null);
      return;
    }
    try {
      const result = await sdk.scenario.getCredentialStatus();
      setStatus(result);
      if (result.decryptionFailed) {
        toast.error('Stored credentials could not be decrypted. The OS keychain may be locked or the vault data is corrupted. Re-enter your credentials to fix this.', {
          duration: 8000
        });
      }
    } catch {
      setStatus(null);
    }
  }, []);
  useEffect(() => {
    if (!active) {
      setApiKey('');
      setSecret('');
      setShowApiKey(false);
      setShowSecret(false);
      setRevealed(null);
      setRevealStored(false);
      return;
    }
    void refreshStatus();
  }, [active, refreshStatus]);
  const handleSave = useCallback(async () => {
    if (!sdk?.scenario?.setCredentials) {
      toast.error('Scenario integration requires the Genesys desktop app.');
      return;
    }
    const trimmedKey = apiKey.trim();
    const trimmedSecret = secret.trim();
    if (!trimmedKey || !trimmedSecret) {
      toast.error('Both API key and secret are required.');
      return;
    }
    setSaving(true);
    try {
      const result_0 = await sdk.scenario.setCredentials(trimmedKey, trimmedSecret);
      if (!result_0.success) {
        toast.error(result_0.message);
        return;
      }
      toast.success('Scenario credentials saved.');
      setApiKey('');
      setSecret('');
      setShowApiKey(false);
      setShowSecret(false);
      setRevealed(null);
      setRevealStored(false);
      void refreshStatus();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [apiKey, refreshStatus, secret]);
  const handleClear = useCallback(async () => {
    if (!sdk?.scenario?.clearCredentials) return;
    setClearing(true);
    try {
      const result_1 = await sdk.scenario.clearCredentials();
      if (!result_1.success) {
        toast.error(result_1.message);
        return;
      }
      toast.success('Scenario credentials removed.');
      setRevealed(null);
      setRevealStored(false);
      void refreshStatus();
    } catch (err_0) {
      toast.error(err_0 instanceof Error ? err_0.message : String(err_0));
    } finally {
      setClearing(false);
    }
  }, [refreshStatus]);
  const openApiKeysPage = useCallback(() => {
    void sdk?.os.openUrl(SCENARIO_SETTINGS_URL);
  }, []);
  const handleToggleReveal = useCallback(async () => {
    if (revealStored) {
      setRevealStored(false);
      setRevealed(null);
      return;
    }
    if (!sdk?.scenario?.revealCredentials) {
      toast.error('Scenario integration requires the Genesys desktop app.');
      return;
    }
    setRevealing(true);
    try {
      setRevealed(await sdk.scenario.revealCredentials());
      setRevealStored(true);
    } catch (err_1) {
      toast.error(err_1 instanceof Error ? err_1.message : String(err_1));
    } finally {
      setRevealing(false);
    }
  }, [revealStored]);
  return <div className="space-y-4">
      <div>
        <div className="text-muted-foreground">Scenario AI</div>
        <div className="font-medium">Skybox, texture, and image generation</div>
        <div className="text-muted-foreground mt-0.5 text-xs">
          Credentials are stored locally with your OS keystore and sent directly from your machine to Scenario. Genesys servers never receive them.
        </div>
      </div>

      <div className="rounded-md border border-border/70 bg-muted/20 px-3 py-3">
        <div className="flex items-center gap-2 text-muted-foreground">
          <span>Status</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" size="icon-xs" variant="ghost" className="size-5" onClick={openApiKeysPage} aria-label="Open Scenario API keys page">
                  <ExternalLinkIcon className="size-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent sideOffset={6}>
                Manage API keys on app.scenario.com/settings/api
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {status?.configured ? <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button type="button" size="icon-xs" variant="ghost" className="size-5" onClick={() => {
                void handleToggleReveal();
              }} disabled={revealing} aria-label={revealStored ? 'Hide stored credentials' : 'Show stored credentials'} aria-pressed={revealStored}>
                    {revealStored ? <EyeOffIcon className="size-3.5" /> : <EyeIcon className="size-3.5" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent sideOffset={6}>
                  {revealStored ? 'Hide stored credentials' : 'Show stored credentials'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider> : null}
        </div>
        <div className="mt-0.5 font-medium">
          {status?.configured ? 'Configured (Secure Storage)' : 'Not Configured'}
        </div>
        {status?.maskedApiKey ? <div className="mt-1 font-mono text-xs text-muted-foreground break-all">
            Key: {revealStored && revealed?.apiKey ? revealed.apiKey : status.maskedApiKey}
          </div> : null}
        {status?.maskedSecret ? <div className="font-mono text-xs text-muted-foreground break-all">
            Secret: {revealStored && revealed?.secret ? revealed.secret : status.maskedSecret}
          </div> : null}
      </div>

      <div className="rounded-md border border-border/70 bg-muted/20 px-3 py-3 space-y-3">
        <div className="text-muted-foreground">
          {status?.configured ? 'Replace Credentials' : 'Add Credentials'}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="scenario-api-key" className="text-xs">API Key</Label>
          <div className="relative">
            <Input id="scenario-api-key" type={showApiKey ? 'text' : 'password'} autoComplete="off" placeholder="Scenario API key" value={apiKey} onChange={e => setApiKey(e.target.value)} disabled={saving} className="pr-8" />
            <Button type="button" size="icon-xs" variant="ghost" className="absolute right-1 top-1/2 size-5 -translate-y-1/2" onClick={() => setShowApiKey(v => !v)} disabled={saving} aria-label={showApiKey ? 'Hide API key' : 'Show API key'} aria-pressed={showApiKey}>
              {showApiKey ? <EyeOffIcon className="size-3.5" /> : <EyeIcon className="size-3.5" />}
            </Button>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="scenario-secret" className="text-xs">Secret</Label>
          <div className="relative">
            <Input id="scenario-secret" type={showSecret ? 'text' : 'password'} autoComplete="off" placeholder="Scenario secret" value={secret} onChange={e_0 => setSecret(e_0.target.value)} disabled={saving} className="pr-8" />
            <Button type="button" size="icon-xs" variant="ghost" className="absolute right-1 top-1/2 size-5 -translate-y-1/2" onClick={() => setShowSecret(v_0 => !v_0)} disabled={saving} aria-label={showSecret ? 'Hide secret' : 'Show secret'} aria-pressed={showSecret}>
              {showSecret ? <EyeOffIcon className="size-3.5" /> : <EyeIcon className="size-3.5" />}
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" size="xs" variant="default" disabled={saving || !apiKey.trim() || !secret.trim()} onClick={() => {
          void handleSave();
        }}>
            <KeyIcon className="size-4" />
            {saving ? 'Saving…' : 'Save Credentials'}
          </Button>
          {status?.configured ? <Button type="button" size="xs" variant="outline" disabled={clearing} onClick={() => {
          void handleClear();
        }}>
              <Trash2Icon className="size-4" />
              {clearing ? 'Removing…' : 'Remove Credentials'}
            </Button> : null}
        </div>
      </div>
    </div>;
}
ScenarioCredentialsSection.displayName = 'ScenarioCredentialsSection';